import React, { useState, useContext, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import {
  TextInput,
  TextAreaInput,
  Base64FileInput,
} from "../../react-hook-form-inputs";
import { PortalizedModalContext } from "./../../PortalizedModal";
import _ from 'underscore.string'
import { renderRailsErrors } from "../../../utils/rails_error_parser";
import {
  BASE_CACHEKEY_MAP,
  addPitchSection,
  updatePitchSection,
  deletePitchSection,
} from "./../queries";
import {
  IconClose,
  IconEdit,
} from './../../../assets/images/icons'

const Investor =({investor, investorEdithandler, investorDeletehandler})=>(
  <div key={investor.id} className="single-existing-notable-inv mb-3">
    <div className="single-existing-pitch-content">
      <div className="existing-notable-inv-icon-box">
        <img src={investor.photo_url} alt="icon" />
      </div>
      <div className="existing-notable-inv-content-box">
        <div className="inner1">
          <p className="existing-notable-inv-title poppins mb-0">
            {investor.name}
          </p>
          <span className="existing-notable-inv-subtitle poppins fs-10">
            {_.prune(investor.title, 25, '...')}
          </span>
        </div>
      </div>
    </div>
    <div className="existing-pitch-actions">
      <img
        onClick={() => investorEdithandler(investor) }
        src={IconEdit}
        alt="edit icon"
      />
      <img
        onClick={() => investorDeletehandler(investor.id) }
        src={IconClose}
        alt="close icon"
      />
    </div>
  </div>
)

const NotableInvestorSchema = Yup.object().shape({
  notable_investor_name: Yup.string().required("Name is required.").max(50, 'Name max length is 50 characters'),
  notable_investor_title: Yup.string().required("Title is required.").max(40, 'Title max length is 40 characters'),
  investor_photo: Yup.object()
    .required("Investor photo is required.")
    .typeError("Investor photo is required."),
  quote: Yup.string(),
});

const NotableInvestors = ({ pitch, pitchableType, pitchableId }) => {
  const deleteInvestorModal = useContext( PortalizedModalContext );
  const notableInvestors = pitch.notable_investors || [];
  const [investorForEdit, setInvestorForEdit] = useState(null);

  const queryClient = useQueryClient();

  const methods = useForm({
    resolver: yupResolver(NotableInvestorSchema),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      notable_investor_name: "",
      notable_investor_title: "",
      investor_photo: null,
      quote: "",
    },
  });

  useEffect(()=>{
    if(investorForEdit){
      methods.setValue("notable_investor_name",investorForEdit.name)
      methods.setValue("notable_investor_title", investorForEdit.title)
      methods.setValue("investor_photo",{ data : investorForEdit.photo_url })
      methods.setValue("quote", investorForEdit.quote)
    }else{
      methods.reset()
    }
  },[investorForEdit, methods])

  const deleteInvestor = useMutation(deletePitchSection, {
    onSuccess: () => {
      queryClient.invalidateQueries([BASE_CACHEKEY_MAP[pitchableType], pitchableId]);
      toast.success(`Notable investor deleted successfully.`);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function investorDeletehandler (id){
    deleteInvestorModal.setModalContent(
      `Delete Investor`,
      <>
        <div className="px-3 pt-3">
          <p>Are you sure you want to delete the investor?</p>
        </div>
        <div className="alert-modal-footer pb-3">
          <button className="zeitung-mono weight-300 mr-3 btn-black-outline" onClick={() => deleteInvestorModal.closeModal()}>Cancel</button>
          <button className="zeitung-mono weight-300 btn-black"
            onClick={() => {
              deleteInvestor.mutate({
                section_id: id,
                type: "notable_investor",
              });
              if(id === investorForEdit?.id)
                setInvestorForEdit(null);
              deleteInvestorModal.closeModal()
            }}>Confirm</button>
        </div>  
      </>
    );
    deleteInvestorModal.openModal();
  }

  const saveNotableInv = useMutation(
    investorForEdit ? updatePitchSection : addPitchSection,
    {
      onSuccess: (values) => {
        methods.reset()
        investorForEdit && setInvestorForEdit(null);
        queryClient.invalidateQueries([BASE_CACHEKEY_MAP[pitchableType], pitchableId]);
        toast.success(`Notable Investor ${investorForEdit ? 'updated' : 'added'} successfully.`);
      },
      onError: (error) => {
        renderRailsErrors(error)
      },
    }
  );

  const onSubmit = (values) => {
    let id = investorForEdit?.id ?? pitchableId;
    let pitch_section = {
      name: values.notable_investor_name,
      title: values.notable_investor_title,
      quote: values.quote,
      photo: values.investor_photo
    }
    if(investorForEdit?.photo_url === values.investor_photo.data)
      delete pitch_section.photo

    saveNotableInv.mutate({
      pitchable_type: pitchableType,
      pitchable_id: id,
      values: {
        type: "notable_investor",
        pitch_section: pitch_section,
      },
    });
  };

  return (
      <div className="notable-inv-maker">
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="add-notable-inv mb-4"
          >
            <h6 className="fs-10 montserrat weight-600">
              Add new notable investor
            </h6>
            <TextInput
              name="notable_investor_name"
              placeholder="Investor name"
              type="text"
            />
            <TextInput
              name="notable_investor_title"
              placeholder="Investor title"
              type="text"
            />
            <Base64FileInput
              name="investor_photo"
              placeholder="Upload investor image"
              accept="image/*"
            />
            <TextAreaInput name="quote" placeholder="Quote" type="text" />
            <button type="submit" className="mx-auto btn btn-outline-dark rounded-0">
              {investorForEdit
                ? "UPADTE NOTABLE INVESTOR"
                : "ADD NOTABLE INVESTOR"}
            </button>
            {investorForEdit && (
              <button
                onClick={() => {
                  methods.reset()
                  setInvestorForEdit(null);
                }}
                type="button"
                className="mx-auto btn btn-outline-dark rounded-0 w-25 ms-3"
              >
                CANCEL
              </button>
            )}
          </form>
        </FormProvider>
        {notableInvestors.length > 0 && 
          <div className="existing-notable-inv">
            <h6 className="fs-10 montserrat weight-600">
              Existing notable investor
            </h6>
            {notableInvestors.map((investor) => (
              <Investor
                key={investor.id}
                investor={investor}
                investorEdithandler={setInvestorForEdit}
                investorDeletehandler={investorDeletehandler}
              />
            ))}
          </div> }
      </div>
  );
};

NotableInvestors.defaultProps = {};

NotableInvestors.propTypes = {
  pitch: PropTypes.object.isRequired,
  pitchableType: PropTypes.string.isRequired,
  pitchableId: PropTypes.string.isRequired,
};

export default NotableInvestors;
